import { setError } from '../store/redux/slices/error/error_slice';
import { logout } from '../store/redux/slices/auth/auth_slice';
import { clearAccessToken, clearRefreshToken } from '../utils/token_functions';

const ERROR_TYPES = {
  AUTH: {
    statusCodes: [401],
    title: 'Authentication Error',
    message: 'Your session has expired. Please sign in again.',
    action: 'Sign In',
    redirectTo: '/signin',
    requiresLogout: true
  },
  PERMISSION: {
    statusCodes: [403],
    title: 'Permission Denied',
    message: 'You do not have permission to perform this action.',
    action: 'Back to Dashboard',
    redirectTo: '/dashboard'
  },
  NOT_FOUND: {
    statusCodes: [404],
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'Go Back',
  },
  SERVER: {
    statusCodes: [500, 502, 503, 504],
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    action: 'Try Again',
  },
  NETWORK: {
    title: 'Network Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    action: 'Retry',
  }
};

const errorMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  if (!action.type.endsWith('/rejected')) return result;

  if (action.meta?.suppressGlobalError) return result;

  const error = action.error || {};
  const payload = action.payload || {};
  const statusCode = payload.statusCode || error.statusCode;

  // Interceptor already tried refresh and it failed — silent logout, no modal.
  const refreshFailed =
    error._refreshFailed ||
    payload._refreshFailed ||
    (typeof payload === 'object' && payload?._refreshFailed);

  if (refreshFailed) {
    clearAccessToken();
    clearRefreshToken();
    store.dispatch(logout());
    return result;
  }

  let errorType = null;
  for (const [type, config] of Object.entries(ERROR_TYPES)) {
    if (config.statusCodes && config.statusCodes.includes(statusCode)) {
      errorType = type;
      break;
    }
  }

  if (!errorType && error.name === 'AxiosError' && error.message === 'Network Error') {
    errorType = 'NETWORK';
  }

  if (errorType) {
    const config = ERROR_TYPES[errorType];

    store.dispatch(setError({
      id: Date.now().toString(),
      type: errorType,
      title: config.title,
      message: payload.message || config.message,
      action: config.action,
      redirectTo: config.redirectTo,
      statusCode,
      source: action.type.split('/')[0],
      timestamp: new Date().toISOString(),
      requiresLogout: config.requiresLogout || false,
      originalError: { message: error.message, payload },
    }));
  }

  return result;
};

export default errorMiddleware;
